/** Old URL → new URL, in one place (plan 1-2).
 *
 *  A rule turns on in the Phase that builds its target page; until then the
 *  old page keeps its address and App.tsx mounts it. Telegram notices and the
 *  desk banner link to /drafts, /redeem/posts?status=failed,
 *  /redeem/requests?state=failed and /jobs?job=…, so every rule keeps the
 *  query (and translates the parts whose meaning moved). */

import { Navigate, Route, useLocation } from 'react-router-dom'

export interface RedirectRule {
  from: string
  /** The Phase that builds the target page and turns this rule on. */
  phase: number
  active: boolean
  to: (query: URLSearchParams) => string
}

/** Statuses that leave review for the schedule once a draft is approved. */
const SCHEDULE_STATUSES = new Set(['scheduled', 'approved', 'published', 'failed'])

function withQuery(path: string, q: URLSearchParams): string {
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

/** A copy of q with `set` applied and `drop` removed, other keys kept. */
function rewrite(q: URLSearchParams, set: Record<string, string>, drop: string[] = []): URLSearchParams {
  const out = new URLSearchParams(q)
  for (const k of drop) out.delete(k)
  for (const [k, v] of Object.entries(set)) out.set(k, v)
  return out
}

export const REDIRECTS: RedirectRule[] = [
  {
    from: '/drafts',
    phase: 3,
    active: true,
    to: (q) => {
      const status = q.get('status') ?? ''
      if (SCHEDULE_STATUSES.has(status)) return withQuery('/schedule', rewrite(q, { source: 'campaign' }))
      // 'all' was the old default; the review page has its own (pending_review).
      if (status === 'all') return withQuery('/review', rewrite(q, {}, ['status']))
      return withQuery('/review', q)
    },
  },
  {
    from: '/posts',
    phase: 3,
    active: true,
    to: (q) => withQuery('/results', rewrite(q, { source: 'campaign' })),
  },
  {
    from: '/redeem/posts',
    phase: 4,
    active: true,
    to: (q) => {
      const status = q.get('status') ?? ''
      if (status === 'draft') return withQuery('/schedule', rewrite(q, { source: 'redeem', lane: 'unscheduled' }, ['status']))
      if (status === 'all') return withQuery('/schedule', rewrite(q, { source: 'redeem' }, ['status']))
      return withQuery('/schedule', rewrite(q, { source: 'redeem' }))
    },
  },
  {
    from: '/redeem/calendar',
    phase: 4,
    active: true,
    to: (q) => withQuery('/schedule/calendar', q),
  },
  { from: '/targets', phase: 5, active: true, to: (q) => withQuery('/accounts', q) },
  { from: '/personas', phase: 5, active: true, to: (q) => withQuery('/accounts', rewrite(q, { tab: 'voice' })) },
]

/** Where an old URL goes. Ignores `active`, so tests can pin a rule before
 *  its Phase turns it on. Null when the path has no rule. */
export function resolveRedirect(pathname: string, search = ''): string | null {
  const rule = REDIRECTS.find((r) => r.from === pathname)
  if (!rule) return null
  return rule.to(new URLSearchParams(search.startsWith('?') ? search.slice(1) : search))
}

/** Paths whose old page has been replaced — App.tsx stops mounting them. */
export function isRedirected(path: string): boolean {
  return REDIRECTS.some((r) => r.active && r.from === path)
}

function RedirectTo({ rule }: { rule: RedirectRule }) {
  const { search } = useLocation()
  return <Navigate replace to={rule.to(new URLSearchParams(search))} />
}

/** The live rules as routes, for App.tsx. */
export function redirectRoutes() {
  return REDIRECTS.filter((r) => r.active).map((r) => (
    <Route key={r.from} path={r.from} element={<RedirectTo rule={r} />} />
  ))
}
